import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowUp, Bot, ExternalLink, MessageCircle, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useKernel } from "./Shortener/hooks/AIKernel";
import "./Assistant.css";

const starterQuestions = [
  "How do I shorten a link?",
  "Can I make a QR code?",
  "What does the AIKernel do?",
];

const answerQuestion = (question, pathname, memory, agentState) => {
  const text = question.toLowerCase();

  if (/\b(qr|code|scan)\b/.test(text)) {
    return {
      text: "After a link is shortened, Linkpin opens its QR code automatically. Choose Download to save the image, or Close to return to your link list.",
      action: { label: "Open the link studio", path: "/#link-studio" },
    };
  }

  if (/\b(shorten|create|make|start|new link|url)\b/.test(text)) {
    return {
      text: pathname === "/"
        ? "Paste a complete http:// or https:// address into the link studio and choose Shorten link. Your recent links are saved in this browser."
        : "The link studio is on the home page. Paste a complete http:// or https:// address there, then choose Shorten link.",
      action: { label: "Go to the link studio", path: "/#link-studio" },
    };
  }

  if (/\b(privacy|stored|storage|data|telemetry|track)\b/.test(text)) {
    return {
      text: "Your recent-link list and the AIKernel's interest/behavior memory are stored in this browser. The AIKernel also sends behavior-memory telemetry to Linkpin's configured service. Clearing link history removes the link list, but does not erase the AIKernel memory.",
      action: { label: "Review the features", path: "/features" },
    };
  }

  if (/\b(analytics|visit|click|performance|report)\b/.test(text)) {
    return {
      text: "The current link list displays the visit and last-visited values available from the shortening service. This interface does not currently provide campaign attribution, geographic reports, or a full analytics dashboard.",
      action: { label: "View the dashboard", path: "/dashboard" },
    };
  }

  if (/\b(ai|kernel|adaptive|recommend|theme|interest)\b/.test(text)) {
    const interest = Object.entries(memory.interests || {})
      .filter(([, weight]) => Number.isFinite(weight))
      .sort((a, b) => b[1] - a[1])[0];
    const interestNote = interest
      ? ` The strongest recorded interest is “${interest[0].replace(/_/g, " ")}.”`
      : " The kernel is still learning from interactions in this session.";

    return {
      text: `The AIKernel is a local, rule-based adaptation layer—not a connected language model. It remembers interaction signals, adapts the visual theme, can reduce animation after repeated rapid clicks, and can emphasize calls to action.${interestNote} This guide uses those kernel signals to make its tips more relevant; your chat message is not sent to an LLM.`,
      action: { label: "Explore Linkpin", path: "/features" },
    };
  }

  if (/\b(help|support|stuck|error|problem|issue)\b/.test(text)) {
    return {
      text: agentState.ui.simplifyAnimations
        ? "I noticed the interface has reduced motion enabled. For link creation, check that the address begins with http:// or https://. If the shortening service is unavailable, try again in a moment."
        : "I can help with shortening links, QR codes, link history, and the adaptive interface. For a failed request, confirm the address begins with http:// or https:// and try again; link creation needs the shortening service to be reachable.",
      action: { label: "Open the link studio", path: "/#link-studio" },
    };
  }

  return {
    text: "I can help with shortening a link, creating its QR code, understanding the link history, or explaining how the local AIKernel adapts the interface. What would you like to know?",
    action: { label: "Explore the features", path: "/features" },
  };
};

const Assistant = () => {
  const { agentState, memory, reportBehavior } = useKernel();
  const prefersReducedMotion = useReducedMotion();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([
    {
      id: "welcome",
      role: "assistant",
      text: "Hi, I’m Linkpin’s local guide. Ask me about shortening a link, QR codes, recent links, or how the adaptive interface works. I use local guidance rules, not an external chat model.",
    },
  ]);
  const messageEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({
      behavior: agentState.ui.simplifyAnimations || prefersReducedMotion ? "auto" : "smooth",
    });
  }, [messages, agentState.ui.simplifyAnimations, prefersReducedMotion]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const sendQuestion = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return;

    const answer = answerQuestion(trimmed, location.pathname, memory, agentState);
    setMessages((current) => [
      ...current,
      { id: `${Date.now()}-user`, role: "user", text: trimmed },
      { id: `${Date.now()}-assistant`, role: "assistant", ...answer },
    ]);
    reportBehavior("assistant_questions", 1, "behavior");
    setQuestion("");
  };

  const followAction = (path) => {
    navigate(path);
    setIsOpen(false);
    reportBehavior("assistant_suggestion_followed", 1, "interest");
  };

  return (
    <div className={`linkpin-assistant${agentState.ui.simplifyAnimations ? " linkpin-assistant--calm" : ""}`}>
      <AnimatePresence>
        {isOpen && (
          <motion.section
            className="assistant-panel"
            aria-label="Linkpin assistant"
            initial={agentState.ui.simplifyAnimations ? false : { opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: agentState.ui.simplifyAnimations ? 0 : 0.18 }}
          >
            <header className="assistant-panel__header">
              <span className="assistant-panel__avatar"><Bot size={19} aria-hidden="true" /></span>
              <div className="assistant-panel__identity">
                <strong>Linkpin guide</strong>
                <span><i /> Local AIKernel guidance</span>
              </div>
              <button className="assistant-icon-button" type="button" onClick={() => setIsOpen(false)} aria-label="Close assistant">
                <X size={18} aria-hidden="true" />
              </button>
            </header>

            <div className="assistant-panel__messages" aria-live="polite">
              {messages.map((message) => (
                <div className={`assistant-message assistant-message--${message.role}`} key={message.id}>
                  <p>{message.text}</p>
                  {message.action && (
                    <button
                      className="assistant-action"
                      type="button"
                      onClick={() => followAction(message.action.path)}
                    >
                      {message.action.label}
                      <ExternalLink size={13} aria-hidden="true" />
                    </button>
                  )}
                </div>
              ))}
              {messages.length === 1 && (
                <div className="assistant-suggestions" aria-label="Suggested questions">
                  {starterQuestions.map((suggestion) => (
                    <button key={suggestion} type="button" onClick={() => sendQuestion(suggestion)}>
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
              <div ref={messageEndRef} />
            </div>

            <form
              className="assistant-composer"
              onSubmit={(event) => {
                event.preventDefault();
                sendQuestion(question);
              }}
            >
              <input
                ref={inputRef}
                aria-label="Ask the Linkpin guide"
                autoComplete="off"
                maxLength={500}
                placeholder="Ask about Linkpin…"
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
              />
              <button type="submit" aria-label="Send message" disabled={!question.trim()}>
                <ArrowUp size={17} aria-hidden="true" />
              </button>
            </form>
            <div className="assistant-panel__disclaimer">Local, rule-based guidance · Messages stay in this session</div>
          </motion.section>
        )}
      </AnimatePresence>

      <button
        className={`assistant-launcher${isOpen ? " assistant-launcher--open" : ""}${agentState.ui.highlightCTAs ? " cta-highlight-glow" : ""}`}
        type="button"
        onClick={() => {
          setIsOpen((open) => !open);
          reportBehavior("assistant_opened", 0.5, "behavior");
        }}
        aria-label={isOpen ? "Close Linkpin guide" : "Open Linkpin guide"}
        aria-expanded={isOpen}
      >
        {isOpen ? <X size={20} aria-hidden="true" /> : <MessageCircle size={20} aria-hidden="true" />}
        {!isOpen && <span>Ask Linkpin</span>}
      </button>
    </div>
  );
};

export default Assistant;
